/**
 * Field-of-view resolution for report snapshot captures.
 *
 * Pure logic, kept free of viewport/service imports so it can be unit tested
 * without loading the VTK module graph.
 */

import type { ISnapshotViewportConfig } from './types';

import type { MeasurementResult } from '@/visualization/domain/sizing/measurement-result';
import { MeasurementUnit } from '@/visualization/domain/sizing/templates/types';

/** FOV = diameter × this factor, so the measure fills ~40% of the frame. */
const AUTO_FOV_DIAMETER_MARGIN_FACTOR = 2.6;
/** Lower clamp — small iliac diameters (~6–10 mm) would otherwise zoom absurdly far. */
const AUTO_FOV_MIN_MM = 90;
/** Upper clamp — safety bound for pathological values. */
const AUTO_FOV_MAX_MM = 160;
/** Measurement context types whose `value` is a diameter in mm. */
const DIAMETER_CONTEXT_TYPES: ReadonlySet<string> = new Set([
    'diameter',
    'offset-diameter',
    'min-max-diameter',
    'custom-diameter',
]);

/**
 * Resolve the field of view for a snapshot config. Returns null when the
 * default viewport behavior (80 mm) should be kept: no `fieldOfViewMm`, or
 * 'auto' without a resolvable diameter measurement.
 */
export function resolveSnapshotFieldOfViewMm(
    config: Pick<ISnapshotViewportConfig, 'fieldOfViewMm' | 'measurementId'>,
    getMeasurement?: (measurementId: string) => MeasurementResult | undefined
): number | null {
    if (typeof config.fieldOfViewMm === 'number') {
        return config.fieldOfViewMm > 0 ? config.fieldOfViewMm : null;
    }
    if (config.fieldOfViewMm !== 'auto') return null;
    if (!config.measurementId || !getMeasurement) return null;

    const measurement = getMeasurement(config.measurementId);
    if (!measurement) return null;
    if (measurement.unit !== MeasurementUnit.MM) return null;
    if (!DIAMETER_CONTEXT_TYPES.has(measurement.context.type)) return null;
    if (!Number.isFinite(measurement.value) || measurement.value <= 0) return null;

    return Math.min(
        AUTO_FOV_MAX_MM,
        Math.max(AUTO_FOV_MIN_MM, measurement.value * AUTO_FOV_DIAMETER_MARGIN_FACTOR)
    );
}
